import { formatCurrency } from '../utils/money'
import ConditionPhotos from '../components/ConditionPhotos'
import type { ConditionPhoto as ConditionPhotoItem } from '../components/ConditionPhotos'
import { useEffect, useState, useRef, useCallback } from 'react'
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import Navbar from '../components/Navbar'
import Modal from '../components/Modal'
import { rentalsApi, paymentApi, fulfillmentsApi, settingsApi } from '../lib/api'
import { SERVICE_FEE_PERCENT } from '../utils/pricing'
import { groupRatesByCarrier } from '../utils/shippingRates'
import { assetUrl } from '../utils/assetUrl'
import ReportDialog from '../components/ReportDialog'

interface Message {
  id: string
  content: string
  createdAt: string
  isRead: boolean
  sender: {
    id: string
    firstName: string
    lastName: string
    avatar?: string
  }
}

interface RentalDetailData {
  id: string
  status: string
  startDate: string
  endDate: string
  totalAmount: number
  itemPrice: number
  serviceFee: number
  depositAmount: number
  isPaid: boolean
  deliveryOption?: string | null
  createdAt: string
  pendingExtensionEndDate?: string | null
  pendingExtensionAmount?: number | null
  pendingExtensionApproved?: boolean
  extensionPaymentIntentId?: string | null
  ownerPayoutTransferId?: string | null
  ownerPayoutAmount?: number | null
  ownerPayoutAt?: string | null
  ownerPayoutFailureReason?: string | null
  shippingCost?: number | null
  shippingCarrier?: string | null
  shippingServiceLevel?: string | null
  renterPaysShipping?: boolean
  shippingLabelTransactionId?: string | null
  shippingLabelUrl?: string | null
  shippingLabelCost?: number | null
  shippingLabelCarrier?: string | null
  shippingLabelServiceLevel?: string | null
  shippingLabelPurchasedAt?: string | null
  shippingLabelFailureReason?: string | null
  shippingLabelRefundId?: string | null
  shippingLabelRefundStatus?: string | null
  outboundTrackingNumber?: string | null
  conditionPhotos?: { atPickup: ConditionPhotoItem[]; atReturn: ConditionPhotoItem[] }
  item: {
    id: string
    title: string
    description: string
    photos?: Array<{ url: string }>
    dailyPrice: number
    weeklyPrice: number | null
    addressApprox: string
    weightLbs?: number | null
    lengthIn?: number | null
    widthIn?: number | null
    heightIn?: number | null
  }
  owner: {
    id: string
    firstName: string
    lastName: string
    avatar?: string
    phone?: string
    verificationStatus: string
  }
  renter: {
    id: string
    firstName: string
    lastName: string
    avatar?: string
    phone?: string
    verificationStatus: string
  }
  pickupLocation?: {
    name: string
    address: string
    city?: string
    state?: string
    zip?: string
    instructions?: string
  } | null
  pickupLocationLabel?: string | null
  // Owner's registered pickup locations and the renter's resolved shipping address,
  // returned by getRentalById so the owner can choose a fulfillment type at approval.
  ownerPickupLocations?: Array<{
    name: string
    address: string
    city?: string
    state?: string
    zip?: string
    isDefault?: boolean
  }>
  renterDropOffLocation?: {
    name: string
    address: string
    city?: string
    state?: string
    zip?: string
    instructions?: string
  } | null
  renterShippingLocations?: Array<{
    name: string
    address: string
    city?: string
    state?: string
    isDefault?: boolean
  }>
  shippingLocationLabel?: string | null
  estimatedPickupDistanceMeters?: number | null
}

const statusLabels: Record<string, string> = {
  PENDING: 'Pending approval',
  APPROVED: 'Approved',
  ACTIVE: 'Active',
  RETURNED: 'Return reported',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  DISPUTED: 'Disputed',
}

const statusClasses: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-blue-100 text-blue-800',
  ACTIVE: 'bg-green-100 text-green-800',
  RETURNED: 'bg-slate-100 text-slate-800',
  COMPLETED: 'bg-slate-100 text-slate-800',
  CANCELLED: 'bg-red-100 text-red-800',
  DISPUTED: 'bg-red-100 text-red-800',
}

const timelineSteps: Array<{ status: string; label: string; completedFor: string[]; activeFor: string[] }> = [
  { status: 'REQUESTED', label: 'Request submitted', completedFor: ['PENDING', 'APPROVED', 'ACTIVE', 'RETURNED', 'COMPLETED'], activeFor: [] },
  { status: 'APPROVED', label: 'Owner approved', completedFor: ['APPROVED', 'ACTIVE', 'RETURNED', 'COMPLETED'], activeFor: [] },
  { status: 'PAID', label: 'Payment complete', completedFor: ['ACTIVE', 'RETURNED', 'COMPLETED'], activeFor: [] },
  { status: 'PICKED_UP', label: 'Item picked up', completedFor: ['ACTIVE', 'RETURNED', 'COMPLETED'], activeFor: [] },
  { status: 'RETURNED', label: 'Renter reported the return', completedFor: ['RETURNED', 'COMPLETED'], activeFor: [] },
  { status: 'COMPLETED', label: 'Owner confirmed the return', completedFor: ['COMPLETED'], activeFor: [] },
]

export default function RentalDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const [rental, setRental] = useState<RentalDetailData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState('')
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [sendingMessage, setSendingMessage] = useState(false)
  const [fulfillmentType, setFulfillmentType] = useState<'pickup' | 'doordash' | 'shipping' | null>(null)
  const [pickupLocationLabel, setPickupLocationLabel] = useState('')
  const [ownerCoversShipping, setOwnerCoversShipping] = useState(false)
  const [showChangeFulfillment, setShowChangeFulfillment] = useState(false)
  const [newFulfillmentType, setNewFulfillmentType] = useState<'pickup' | 'doordash' | 'shipping' | null>(null)
  const [newPickupLocationLabel, setNewPickupLocationLabel] = useState('')
  const [newOwnerCoversShipping, setNewOwnerCoversShipping] = useState(false)
  const [showChangeShipping, setShowChangeShipping] = useState(false)
  const [newShippingLocationLabel, setNewShippingLocationLabel] = useState('')
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelLoading, setCancelLoading] = useState(false)
  const [cancelError, setCancelError] = useState('')
  const [showExtendModal, setShowExtendModal] = useState(false)
  const [extendNewEndDate, setExtendNewEndDate] = useState('')
  const [extendLoading, setExtendLoading] = useState(false)
  const [extendError, setExtendError] = useState('')
  const [activeRentalAction, setActiveRentalAction] = useState<'return' | 'problem' | 'review'>('return')
  const [doorDashLimits, setDoorDashLimits] = useState({ maxWeight: 50, maxDimension: 36 })
  // The approval panel picks a real carrier and buys (or simulates buying) the
  // label as part of approving -- a live rate list, not a single auto-picked
  // estimate. The "Change fulfillment type" modal below still uses the older
  // single-estimate shippingQuote-style shape (newShippingQuote) unchanged;
  // moving that flow to real carrier choice too is a documented follow-up,
  // not part of this pass.
  const [shippingRates, setShippingRates] = useState<{ loading: boolean; available: boolean; rates: Array<{ rateId: string; amount: string; carrier: string | null; serviceLevel: string | null; estimatedDays: number | null }>; reason?: string; distanceMiles?: number | null; fromAddress?: string; toAddress?: string; item?: { weightLbs?: number | null; lengthIn?: number | null; widthIn?: number | null; heightIn?: number | null } } | null>(null)
  const [selectedRateId, setSelectedRateId] = useState('')
  // A carrier (UPS, USPS...) can offer several shipping methods (Ground,
  // Priority...), each its own price -- tracked separately from selectedRateId
  // so the UI can offer "pick a carrier, then a method" as two dropdowns
  // instead of one combined list mixing both together.
  const [selectedCarrier, setSelectedCarrier] = useState('')
  const [newShippingQuote, setNewShippingQuote] = useState<{ loading: boolean; available: boolean; amount?: string; carrier?: string; reason?: string; distanceMiles?: number | null; fromAddress?: string; toAddress?: string } | null>(null)
  const [showReportProblemModal, setShowReportProblemModal] = useState(false)
  const [reportingUser, setReportingUser] = useState(false)
  const [problemDescription, setProblemDescription] = useState('')
  const [problemSubmitting, setProblemSubmitting] = useState(false)
  const [problemError, setProblemError] = useState('')
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [reviewRating, setReviewRating] = useState(5)
  const [reviewContent, setReviewContent] = useState('')
  const [reviewWouldRentAgain, setReviewWouldRentAgain] = useState(false)
  const [reviewSubmitting, setReviewSubmitting] = useState(false)
  const [reviewError, setReviewError] = useState('')
  const [reviewSubmitted, setReviewSubmitted] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesScrollRef = useRef<HTMLDivElement>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const prevMessageCountRef = useRef(0)

  const fetchMessages = useCallback(async () => {
    if (!id) return
    try {
      const res = await rentalsApi.getMessages(id)
      if (res.data.success) {
        setMessages(res.data.data || [])
      }
    } catch (err: any) {
      // Back off on rate limit — stop polling so we don't lock the user out.
      if (err?.response?.status === 429 && pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [id])

  useEffect(() => {
    if (id) {
      fetchRental(id)
      fetchMessages()
    }
  }, [id, fetchMessages])

  // Poll for new messages every 15 seconds. Stop on 429 (back-off).
  //
  // Depends on Boolean(rental), NOT rental itself -- the rental-refresh poll below calls
  // setRental with a brand-new object every 10s, and this effect used to depend on the
  // whole `rental` object, so React tore down and recreated this interval every single
  // time the rental refreshed. Since that happens faster (10s) than this interval's own
  // period (15s), the interval was reset before it ever got to fire on its own --
  // messages effectively never auto-refreshed once the rental-poll fix shipped.
  useEffect(() => {
    if (!rental) return
    pollRef.current = setInterval(fetchMessages, 15000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(rental), fetchMessages])

  // Jump to the message thread when arriving via #messages (the Messages inbox links
  // here so clicking a conversation actually lands on the conversation, not just the
  // top of the rental page). Gated on the rental having loaded -- scrolling to an
  // element that doesn't exist yet is a no-op.
  //
  // Scrolls twice: content above the target (the item photo, the message list itself
  // resolving from "loading" to its real height, etc.) can still be settling when the
  // rental first becomes available, since messages/images fetch independently and
  // finish slightly later. A single scrollIntoView locks in a destination based on
  // the layout at that instant, so a shift afterward leaves it off by however much
  // things moved. The second call re-measures and corrects; it's a no-op if nothing
  // shifted.
  useEffect(() => {
    const targetId = location.hash.slice(1)
    if (!rental || !['messages', 'payment-summary', 'pickup-location'].includes(targetId)) return
    const scroll = () => document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    scroll()
    const correction = setTimeout(scroll, 500)
    return () => clearTimeout(correction)
  }, [Boolean(rental), location.hash, id])

  // Poll the rental itself every 10s, same pattern PickupTracker.tsx/DigitalHandshake.tsx
  // already use -- without this, an action the OTHER party takes (approve, pay, mark
  // returned, confirm) never shows up here without a manual reload. Stops once the
  // rental reaches a terminal state.
  useEffect(() => {
    if (!id || !rental) return
    if (rental.status === 'COMPLETED' || rental.status === 'CANCELLED') return
    const interval = setInterval(() => fetchRental(id, true), 10000)
    return () => clearInterval(interval)
  }, [id, rental?.status])

  // Only auto-scroll when a new message actually arrived AND the user is
  // already near the bottom — never on a no-op poll (which was hijacking the
  // user's scroll position every cycle).
  useEffect(() => {
    const container = messagesScrollRef.current
    if (!container || messages.length === 0) {
      prevMessageCountRef.current = messages.length
      return
    }
    const grew = messages.length > prevMessageCountRef.current
    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 120
    if (grew && nearBottom) {
      // block: 'nearest' keeps this confined to the message thread's own scroll
      // container -- without it, scrollIntoView cascades up to the outer page
      // scroll too, dragging the viewport down into whatever section sits below
      // the message box (e.g. the Pickup Location card) every time a message sends.
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
    prevMessageCountRef.current = messages.length
  }, [messages])

  useEffect(() => {
    settingsApi.getAll()
      .then((res) => {
        const settings: Array<{ key: string; value: string }> = res.data.data || []
        const maxWeight = Number(settings.find((s) => s.key === 'DOORDASH_MAX_WEIGHT_LBS')?.value)
        const maxDimension = Number(settings.find((s) => s.key === 'DOORDASH_MAX_DIMENSION_IN')?.value)
        setDoorDashLimits({
          maxWeight: Number.isFinite(maxWeight) ? maxWeight : 50,
          maxDimension: Number.isFinite(maxDimension) ? maxDimension : 36,
        })
      })
      .catch(() => {})
  }, [])

  // Live re-quote for the owner's initial approval panel as they pick a pickup
  // location -- a real, freshly-quoted carrier list. The owner picks one
  // (selectedRateId) and Approve buys/simulates that exact label.
  useEffect(() => {
    if (!id || fulfillmentType !== 'shipping' || rental?.status !== 'PENDING') {
      setShippingRates(null)
      setSelectedRateId('')
      setSelectedCarrier('')
      return
    }
    let cancelled = false
    setShippingRates({ loading: true, available: false, rates: [] })
    rentalsApi.getShippingRates(id, pickupLocationLabel || undefined)
      .then((res) => {
        if (cancelled) return
        const data = res.data.data
        setShippingRates({ loading: false, ...data })
        setSelectedRateId(data.rates?.[0]?.rateId || '')
        setSelectedCarrier(data.rates?.[0]?.carrier || '')
      })
      .catch(() => {
        if (cancelled) return
        setShippingRates({ loading: false, available: false, rates: [], reason: 'Could not load shipping rates right now.' })
        setSelectedRateId('')
        setSelectedCarrier('')
      })
    return () => { cancelled = true }
  }, [id, fulfillmentType, pickupLocationLabel, rental?.status])

  // Same, for the "Change fulfillment type" modal.
  useEffect(() => {
    if (!id || !showChangeFulfillment || newFulfillmentType !== 'shipping') {
      setNewShippingQuote(null)
      return
    }
    let cancelled = false
    setNewShippingQuote({ loading: true, available: false })
    rentalsApi.getShippingQuote(id, newPickupLocationLabel || undefined)
      .then((res) => {
        if (cancelled) return
        setNewShippingQuote({ loading: false, ...res.data.data })
      })
      .catch(() => {
        if (cancelled) return
        setNewShippingQuote({ loading: false, available: false, reason: 'Could not load a shipping estimate right now.' })
      })
    return () => { cancelled = true }
  }, [id, showChangeFulfillment, newFulfillmentType, newPickupLocationLabel])

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !id) return
    setSendingMessage(true)
    try {
      const res = await rentalsApi.sendMessage(id, newMessage.trim())
      if (res.data.success) {
        setMessages(prev => [...prev, res.data.data])
        setNewMessage('')
      }
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Failed to send message')
    } finally {
      setSendingMessage(false)
    }
  }

  const fetchRental = async (rentalId: string, isBackgroundRefresh = false) => {
    if (!isBackgroundRefresh) setIsLoading(true)
    setError('')
    try {
      const response = await rentalsApi.getById(rentalId)
      if (response.data.success) {
        setRental(response.data.data)
        // Pre-select the fulfillment method the renter actually requested -- skipped on
        // background polls so a poll firing mid-edit doesn't clobber an in-progress
        // fulfillment-type selection the owner hasn't submitted yet.
        if (!isBackgroundRefresh) {
          const requestedType = (response.data.data.deliveryOption || 'pickup') as 'pickup' | 'doordash' | 'shipping'
          setFulfillmentType(requestedType)
          setNewFulfillmentType(requestedType)
          // Pre-select the owner's default location (the list shows it as "(default)"). Left
          // empty, the dropdown only LOOKED like it had the first entry selected while the
          // request sent nothing, so the server quietly used the item's own label instead.
          const locations = response.data.data.ownerPickupLocations || []
          const defaultName = (locations.find((l: { isDefault?: boolean }) => l.isDefault) || locations[0])?.name || ''
          setPickupLocationLabel(defaultName)
          setNewPickupLocationLabel(response.data.data.pickupLocationLabel || defaultName)
        }
      } else if (!isBackgroundRefresh) {
        setError(response.data.error || 'Could not load rental.')
      }
    } catch (err: any) {
      if (!isBackgroundRefresh) setError(err?.response?.data?.error || 'Could not load rental.')
    } finally {
      if (!isBackgroundRefresh) setIsLoading(false)
    }
  }

  const handleAction = async (action: () => Promise<any>) => {
    if (!rental) return
    setActionLoading(true)
    setActionError('')
    try {
      await action()
      await fetchRental(rental.id)
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Action failed.')
    } finally {
      setActionLoading(false)
    }
  }

  const handlePayNow = () => handleAction(async () => {
    if (!rental) return
    const intent = await paymentApi.createIntent(rental.id)
    if (intent?.data?.data?.clientSecret) {
      navigate('/checkout', {
        state: {
          rentalId: rental.id,
          clientSecret: intent.data.data.clientSecret,
          returnUrl: `/checkout/success?rental_id=${rental.id}`,
        },
      })
    } else {
      throw new Error('Could not start payment')
    }
  })

  const handleTrack = async () => {
    if (!rental) return
    try {
      const fulResponse = await fulfillmentsApi.getByRental(rental.id)
      if (fulResponse.data.success && fulResponse.data.data) {
        const ful = fulResponse.data.data
        navigate(ful.fulfillmentType === 'DELIVERY' ? `/fulfillment/${ful.id}/shipping` : `/fulfillment/${ful.id}`)
      } else {
        setActionError('Fulfillment record not found. Please contact support.')
      }
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Could not load fulfillment')
    }
  }

  const handleStartReturn = async () => {
    if (!rental) return
    try {
      const fulResponse = await fulfillmentsApi.getByRental(rental.id)
      if (fulResponse.data.success && fulResponse.data.data) {
        navigate(`/fulfillment/${fulResponse.data.data.id}/inspection`, { state: { returnType: true } })
      } else {
        setActionError('Fulfillment record not found. Please contact support.')
      }
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Could not load fulfillment')
    }
  }

  const handleActiveRentalGo = () => {
    if (activeRentalAction === 'return') {
      handleStartReturn()
    } else if (activeRentalAction === 'problem') {
      setProblemDescription('')
      setProblemError('')
      setShowReportProblemModal(true)
    } else if (activeRentalAction === 'review') {
      setReviewRating(5)
      setReviewContent('')
      setReviewWouldRentAgain(false)
      setReviewError('')
      setShowReviewModal(true)
    }
  }

  const handleSubmitProblem = async () => {
    if (!rental) return
    if (!problemDescription.trim()) {
      setProblemError('Please describe the problem')
      return
    }
    setProblemSubmitting(true)
    setProblemError('')
    try {
      // Reuses the message thread (already emails+notifies the other party) rather
      // than a bespoke dispute-resolution flow -- see report-problem route comment.
      await rentalsApi.sendMessage(rental.id, `Problem reported: ${problemDescription.trim()}`)
      await rentalsApi.reportProblem(rental.id)
      setShowReportProblemModal(false)
      await fetchRental(rental.id)
    } catch (err: any) {
      setProblemError(err?.response?.data?.error || 'Failed to report problem')
    } finally {
      setProblemSubmitting(false)
    }
  }

  const handleSubmitReview = async () => {
    if (!rental) return
    setReviewSubmitting(true)
    setReviewError('')
    try {
      await rentalsApi.createReview(rental.id, {
        rating: reviewRating,
        content: reviewContent.trim() || undefined,
        wouldRentAgain: reviewWouldRentAgain,
      })
      setShowReviewModal(false)
      setReviewSubmitted(true)
    } catch (err: any) {
      setReviewError(err?.response?.data?.error || 'Failed to submit review')
    } finally {
      setReviewSubmitting(false)
    }
  }

  const confirmCancelRental = async () => {
    if (!rental) return
    setCancelLoading(true)
    setCancelError('')
    try {
      const reason = cancelReason.trim()
      if (reason) {
        await rentalsApi.sendMessage(rental.id, `Cancellation reason: ${reason}`)
        await fetchMessages()
      }
      await rentalsApi.cancel(rental.id)
      await fetchRental(rental.id)
      setShowCancelModal(false)
      setCancelReason('')
    } catch (err: any) {
      setCancelError(err?.response?.data?.error || 'Failed to cancel the rental.')
    } finally {
      setCancelLoading(false)
    }
  }

  const confirmRequestExtension = async () => {
    if (!rental || !extendNewEndDate) return
    setExtendLoading(true)
    setExtendError('')
    try {
      await rentalsApi.requestExtension(rental.id, extendNewEndDate)
      await fetchRental(rental.id)
      setShowExtendModal(false)
      setExtendNewEndDate('')
    } catch (err: any) {
      setExtendError(err?.response?.data?.error || 'Failed to request extension.')
    } finally {
      setExtendLoading(false)
    }
  }

  const handlePayForExtension = async () => {
    if (!rental) return
    setActionLoading(true)
    setActionError('')
    try {
      const intent = await paymentApi.createExtensionIntent(rental.id)
      if (intent?.data?.data?.clientSecret) {
        navigate('/checkout', {
          state: {
            rentalId: rental.id,
            clientSecret: intent.data.data.clientSecret,
            returnUrl: `/checkout/success?rental_id=${rental.id}&type=extension`,
            paymentType: 'extension',
          },
        })
      } else {
        throw new Error('Could not start payment')
      }
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Could not start payment')
    } finally {
      setActionLoading(false)
    }
  }

  const formatDate = (value: string) => {
    return new Date(value).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }


  const renderImage = () => {
    const url = rental?.item.photos?.[0]?.url
    if (!url) {
      return (
        <div className="w-full h-64 bg-[var(--bg-tint)] rounded-xl flex items-center justify-center text-5xl">
          <i className="mdi mdi-hammer-screwdriver text-[var(--forest-green)]"></i>
        </div>
      )
    }
    return (
      <img
        src={assetUrl(url)}
        alt={rental.item.title}
        className="w-full h-64 object-cover rounded-xl"
        onError={(e) => { e.currentTarget.style.display = 'none' }}
      />
    )
  }

  const renderActions = () => {
    if (!rental || !user) return null
    const isOwner = rental.owner.id === user.id
    const isRenter = rental.renter.id === user.id
    const isPickupFulfillment = !rental.deliveryOption || rental.deliveryOption === 'pickup'

    // Missing weight/dimensions never blocks DoorDash -- only an item the owner
    // actually measured as too big/heavy does. Mirrors doorDashEligibility.ts server-side.
    const doorDashOversizedReason = (() => {
      const { weightLbs, lengthIn, widthIn, heightIn } = rental.item
      if (weightLbs != null && weightLbs > doorDashLimits.maxWeight) {
        return `This item's weight (${weightLbs} lbs) exceeds the ${doorDashLimits.maxWeight} lb DoorDash limit.`
      }
      const dims = [lengthIn, widthIn, heightIn].filter((d): d is number => d != null)
      const oversized = dims.find((d) => d > doorDashLimits.maxDimension)
      if (oversized !== undefined) {
        return `This item's dimensions exceed the ${doorDashLimits.maxDimension}" DoorDash limit.`
      }
      return null
    })()

    return (
      <>
      <div className="flex flex-wrap gap-3 mt-6">
        {isOwner && rental.status === 'PENDING' && (
          <>
            <div className="w-full bg-[var(--bg-tint)] rounded-xl p-4 mb-2">
              <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">
                How will the renter get this item?
              </h3>
              <div className="flex flex-col sm:flex-row gap-3">
                {([
                  { key: 'pickup', label: 'Pickup', icon: 'mdi-map-marker' },
                  { key: 'shipping', label: 'Shipping', icon: 'mdi-truck' },
                  { key: 'doordash', label: 'DoorDash', icon: 'mdi-moped' },
                ] as const).map((opt) => {
                  const hasOwnerPickup = (rental.ownerPickupLocations?.length ?? 0) > 0
                  const hasRenterDropOff = !!rental.renterDropOffLocation
                  const enabled =
                    opt.key === 'pickup' ? hasOwnerPickup
                    : opt.key === 'doordash' ? (hasOwnerPickup && hasRenterDropOff && !doorDashOversizedReason)
                    : hasRenterDropOff // shipping
                  return (
                    <label
                      key={opt.key}
                      className={`flex-1 flex items-center gap-3 p-3 rounded-lg border ${
                        fulfillmentType === opt.key
                          ? 'border-[var(--forest-green)] bg-white'
                          : 'border-[var(--border)] bg-white'
                      } ${enabled ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                    >
                      <input
                        type="radio"
                        name="approvalFulfillmentType"
                        value={opt.key}
                        checked={fulfillmentType === opt.key}
                        disabled={!enabled || actionLoading}
                        onChange={() => setFulfillmentType(opt.key)}
                        className="accent-[var(--forest-green)]"
                      />
                      <i className={`mdi ${opt.icon} text-[var(--forest-green)]`}></i>
                      <span className="text-sm text-[var(--text-primary)]">{opt.label}</span>
                    </label>
                  )
                })}
              </div>

              {/* Pickup-type missing: show the same green notice as the List Item page. */}
              {(rental.ownerPickupLocations?.length ?? 0) === 0 && (
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', padding: '0.75rem', background: 'var(--bg-tint)', borderRadius: '8px', marginTop: '0.75rem' }}>
                  No pickup locations saved.{' '}
                  <Link to="/profile" style={{ color: 'var(--forest-green)', fontWeight: 500 }}>Add one in Profile &gt; Locations</Link>
                  {' '}so renters know where to pick up.
                </p>
              )}

              {/* Renter missing a shipping address: explain why DoorDash/Shipping are disabled. */}
              {!rental.renterDropOffLocation && (
                <p className="text-xs text-[var(--text-muted)] mt-3">
                  The renter hasn&apos;t added a shipping address, so DoorDash and Shipping aren&apos;t available. Ask them to add one in Profile &gt; Locations, or choose pickup.
                </p>
              )}

              {/* Item too big/heavy: explain why DoorDash specifically is disabled. */}
              {doorDashOversizedReason && (
                <p className="text-xs text-[var(--text-muted)] mt-3">
                  {doorDashOversizedReason} Shipping is still available.
                </p>
              )}

              {/* Estimated at request time from the renter's rough profile location —
                  a heads-up, not a hard block, since it's only city-level accurate. */}
              {!rental.renterDropOffLocation &&
                (rental.estimatedPickupDistanceMeters ?? 0) > 160934 && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
                  This renter appears to be about {Math.round((rental.estimatedPickupDistanceMeters ?? 0) / 1609.34)} miles away — pickup may not be practical. Consider asking them to add a shipping address.
                </p>
              )}

              {/* Shipping: pick a real carrier, see a receipt-style breakdown, and
                  Approve buys (or, by default, simulates buying) that exact
                  label as part of approving -- addresses/distance/item size are
                  shown regardless of whether a rate is available, since they're
                  useful context either way (e.g. deciding whether to switch to
                  Pickup instead). */}
              {fulfillmentType === 'shipping' && shippingRates && (
                <div className="mt-3 p-3 rounded-lg bg-white border border-[var(--border)] space-y-2">
                  {shippingRates.loading ? (
                    <p className="text-xs text-[var(--text-secondary)]">Getting live shipping rates...</p>
                  ) : (
                    <>
                      {(shippingRates.fromAddress || shippingRates.toAddress || shippingRates.distanceMiles != null) && (
                        <div className="text-xs text-[var(--text-secondary)] space-y-0.5">
                          {shippingRates.fromAddress && <div>Ship from: {shippingRates.fromAddress}</div>}
                          {shippingRates.toAddress && <div>Ship to: {shippingRates.toAddress}</div>}
                          {shippingRates.distanceMiles != null && <div>Distance: {Math.round(shippingRates.distanceMiles).toLocaleString()} mi</div>}
                        </div>
                      )}
                      {(rental.item.weightLbs != null || rental.item.lengthIn != null) && (
                        <div className="text-xs text-[var(--text-secondary)]">
                          Item: {rental.item.weightLbs != null ? `${rental.item.weightLbs} lbs` : 'weight not set'}
                          {rental.item.lengthIn != null && rental.item.widthIn != null && rental.item.heightIn != null
                            ? ` — ${rental.item.lengthIn}"×${rental.item.widthIn}"×${rental.item.heightIn}"`
                            : ''}
                        </div>
                      )}
                      {shippingRates.available && shippingRates.rates.length > 0 ? (
                        (() => {
                          const carrierGroups = groupRatesByCarrier(shippingRates.rates)
                          const currentGroup = carrierGroups.find((g) => g.carrier === selectedCarrier) || carrierGroups[0]
                          const selected = currentGroup?.rates.find((r) => r.rateId === selectedRateId) || currentGroup?.rates[0]
                          const shippingAmount = selected ? Number(selected.amount) : 0
                          // rental.itemPrice/serviceFee arrive over the wire as Prisma
                          // Decimal-serialized strings despite the TS type saying
                          // number (formatCurrency below already defends against this
                          // per-field; this raw sum needs the same Number() coercion
                          // or it silently does string concatenation instead of math).
                          const grandTotal = Number(rental.itemPrice) + Number(rental.serviceFee) + (ownerCoversShipping ? 0 : shippingAmount)
                          return (
                            <>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label htmlFor="approvalCarrier" className="block text-xs text-[var(--text-secondary)] mb-1">Carrier</label>
                                  <select
                                    id="approvalCarrier"
                                    value={currentGroup?.carrier || ''}
                                    onChange={(e) => {
                                      const group = carrierGroups.find((g) => g.carrier === e.target.value)
                                      setSelectedCarrier(e.target.value)
                                      setSelectedRateId(group?.rates[0]?.rateId || '')
                                    }}
                                    className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
                                  >
                                    {carrierGroups.map((g) => (
                                      <option key={g.carrier} value={g.carrier}>{g.carrier}</option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label htmlFor="approvalServiceLevel" className="block text-xs text-[var(--text-secondary)] mb-1">Shipping method</label>
                                  <select
                                    id="approvalServiceLevel"
                                    value={selectedRateId}
                                    onChange={(e) => setSelectedRateId(e.target.value)}
                                    className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
                                  >
                                    {currentGroup?.rates.map((r) => (
                                      <option key={r.rateId} value={r.rateId}>
                                        {r.serviceLevel || 'Standard'} — {formatCurrency(Number(r.amount))}
                                        {r.estimatedDays != null ? ` (${r.estimatedDays}d)` : ''}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className="text-xs text-[var(--text-secondary)] space-y-1 border-t border-[var(--border)] pt-2">
                                <div className="flex justify-between"><span>Item price</span><span>{formatCurrency(rental.itemPrice)}</span></div>
                                <div className="flex justify-between"><span>Service fee</span><span>{formatCurrency(rental.serviceFee)}</span></div>
                                <div className="flex justify-between">
                                  <span>Shipping{selected?.carrier ? ` (${selected.carrier})` : ''}{ownerCoversShipping ? ' — covered by you' : ''}</span>
                                  <span className={ownerCoversShipping ? 'line-through text-[var(--text-muted)]' : ''}>{formatCurrency(shippingAmount)}</span>
                                </div>
                                <div className="flex justify-between font-semibold text-[var(--text-primary)] border-t border-[var(--border)] pt-1">
                                  <span>Grand total</span><span>{formatCurrency(grandTotal)}</span>
                                </div>
                              </div>
                              <label className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                                <input
                                  type="checkbox"
                                  checked={ownerCoversShipping}
                                  onChange={(e) => setOwnerCoversShipping(e.target.checked)}
                                  className="accent-[var(--forest-green)]"
                                />
                                I will cover the shipping cost
                              </label>
                            </>
                          )
                        })()
                      ) : (
                        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1">
                          <p className="font-medium">Can&apos;t approve as Shipping right now — no carrier rate is available for this route.</p>
                          <p>{shippingRates.reason}</p>
                          <p>Consider Pickup, or contact the renter.</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Every fulfillment type involves one of the owner's saved locations --
                  pickup/doordash meet the renter there, shipping ships FROM there.
                  Also drives the live shipping re-quote effect above, keyed on
                  pickupLocationLabel. */}
              {fulfillmentType && (rental.ownerPickupLocations?.length ?? 0) > 0 && (
                <div className="mt-3">
                  <label htmlFor="pickupLocationLabel" className="block text-xs text-[var(--text-secondary)] mb-1">
                    {fulfillmentType === 'shipping' ? 'Ship from location' : 'Pickup location'}
                  </label>
                  <select
                    id="pickupLocationLabel"
                    value={pickupLocationLabel}
                    onChange={(e) => setPickupLocationLabel(e.target.value)}
                    style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '0.95rem', backgroundColor: 'white' }}
                  >
                    {rental.ownerPickupLocations!.map((loc) => (
                      <option key={loc.name} value={loc.name}>
                        {loc.name}{loc.address ? ` — ${loc.address}` : ''}{loc.isDefault ? ' (default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {!fulfillmentType && (
              <p className="w-full text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Select how the renter will get this item above before approving.
              </p>
            )}

            <button
              disabled={
                actionLoading ||
                !fulfillmentType ||
                (fulfillmentType === 'shipping' && (!shippingRates || shippingRates.loading || !shippingRates.available || !selectedRateId))
              }
              onClick={() =>
                handleAction(() =>
                  rentalsApi.approve(rental.id, {
                    fulfillmentType: fulfillmentType!,
                    pickupLocationLabel: pickupLocationLabel || undefined,
                    renterPaysShipping: !ownerCoversShipping,
                    rateId: fulfillmentType === 'shipping' ? selectedRateId : undefined,
                  })
                )
              }
              className="btn btn-primary"
            >
              {fulfillmentType === 'shipping' ? 'Approve & Buy Label' : 'Approve rental'}
            </button>
            <button
              disabled={actionLoading}
              onClick={() => handleAction(() => rentalsApi.decline(rental.id, ''))}
              className="btn btn-secondary"
            >
              Decline
            </button>
          </>
        )}

        {isRenter && rental.status === 'APPROVED' && !rental.isPaid && (
          <button
            disabled={actionLoading}
            onClick={handlePayNow}
            className="btn btn-primary btn-sm"
          >
            Pay now
          </button>
        )}

        {/* Also available for RETURNED/COMPLETED -- previously this was the only way to
            reach the fulfillment page (QR code, handshake status, photos, audit trail)
            and it disappeared the moment the rental finished, with no way back to it. */}
        {(isOwner || isRenter) && ['APPROVED', 'ACTIVE', 'RETURNED', 'COMPLETED'].includes(rental.status) && (
          <button
            disabled={actionLoading}
            onClick={handleTrack}
            className="btn btn-accent btn-sm"
          >
            <i className={`mdi ${rental.status === 'COMPLETED' ? 'mdi-history' : rental.status === 'ACTIVE' || rental.status === 'RETURNED' ? 'mdi-map-marker-path' : isPickupFulfillment ? 'mdi-package-variant-closed' : 'mdi-truck'} mr-1`}></i>
            {rental.status === 'COMPLETED' ? (isPickupFulfillment ? 'View pickup details' : 'View shipment details') : rental.status === 'ACTIVE' || rental.status === 'RETURNED' ? 'Track rental' : isPickupFulfillment ? 'Track pickup' : 'Track shipment'}
          </button>
        )}

        {(isOwner || isRenter) && (rental.status === 'PENDING' || rental.status === 'APPROVED') && (
          <button
            disabled={actionLoading}
            onClick={() => {
              setCancelReason('')
              setCancelError('')
              setShowCancelModal(true)
            }}
            className="btn btn-danger btn-sm sm:ml-auto"
          >
            <i className="mdi mdi-cancel mr-1"></i>
            Cancel rental
          </button>
        )}

        {isRenter && rental.status === 'ACTIVE' && !rental.pendingExtensionEndDate && (
          <button
            type="button"
            disabled={actionLoading}
            onClick={() => {
              setExtendError('')
              setExtendNewEndDate('')
              setShowExtendModal(true)
            }}
            className="btn btn-secondary btn-sm"
          >
            <i className="mdi mdi-calendar-plus mr-1"></i>
            Extend rental
          </button>
        )}

        {rental.pendingExtensionEndDate && (
          <div className="w-full bg-[var(--bg-tint)] rounded-xl p-4">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
              Extension requested: until {formatDate(rental.pendingExtensionEndDate)} ({formatCurrency(rental.pendingExtensionAmount || 0)})
            </h3>

            {isOwner && !rental.pendingExtensionApproved && (
              <>
                <p className="text-xs text-[var(--text-secondary)] mb-3">
                  The renter would like to extend this rental. Approve to let them pay for the extra days, or decline to keep the original end date.
                </p>
                <div className="flex gap-3">
                  <button
                    disabled={actionLoading}
                    onClick={() => handleAction(() => rentalsApi.approveExtension(rental.id))}
                    className="btn btn-primary btn-sm"
                  >
                    Approve extension
                  </button>
                  <button
                    disabled={actionLoading}
                    onClick={() => handleAction(() => rentalsApi.declineExtension(rental.id))}
                    className="btn btn-secondary btn-sm"
                  >
                    Decline
                  </button>
                </div>
              </>
            )}

            {isOwner && rental.pendingExtensionApproved && (
              <p className="text-xs text-[var(--text-secondary)]">
                You approved this extension. Waiting for the renter to pay.
              </p>
            )}

            {isRenter && !rental.pendingExtensionApproved && (
              <>
                <p className="text-xs text-[var(--text-secondary)] mb-3">
                  Awaiting owner approval.
                </p>
                <button
                  disabled={actionLoading}
                  onClick={() => handleAction(() => rentalsApi.cancelExtensionRequest(rental.id))}
                  className="btn btn-secondary btn-sm"
                >
                  Cancel request
                </button>
              </>
            )}

            {isRenter && rental.pendingExtensionApproved && (
              <>
                <p className="text-xs text-[var(--text-secondary)] mb-3">
                  Approved! Pay {formatCurrency(rental.pendingExtensionAmount || 0)} to confirm the new end date.
                </p>
                <button
                  disabled={actionLoading}
                  onClick={handlePayForExtension}
                  className="btn btn-primary btn-sm"
                >
                  Pay for extension
                </button>
              </>
            )}
          </div>
        )}

        {isOwner && rental.status === 'APPROVED' && (
          <div className="w-full">
            {!showChangeFulfillment ? (
              <button
                type="button"
                disabled={actionLoading}
                onClick={() => {
                  // Already pre-set to the rental's current deliveryOption in
                  // fetchRental -- don't clear it back to unselected here.
                  setNewPickupLocationLabel('')
                  setShowChangeFulfillment(true)
                }}
                className="text-sm font-medium text-[var(--forest-green)] hover:underline"
              >
                Change fulfillment type
              </button>
            ) : (
              <div className="w-full bg-[var(--bg-tint)] rounded-xl p-4">
                <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
                  Change how the renter gets this item
                </h3>
                <p className="text-xs text-[var(--text-secondary)] mb-3">
                  Currently set to {rental.deliveryOption || 'pickup'}. Works even if already paid.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  {([
                    { key: 'pickup', label: 'Pickup', icon: 'mdi-map-marker' },
                    { key: 'shipping', label: 'Shipping', icon: 'mdi-truck' },
                    { key: 'doordash', label: 'DoorDash', icon: 'mdi-moped' },
                  ] as const).map((opt) => {
                    const hasOwnerPickup = (rental.ownerPickupLocations?.length ?? 0) > 0
                    const hasRenterDropOff = !!rental.renterDropOffLocation
                    const enabled =
                      opt.key === 'pickup' ? hasOwnerPickup
                      : opt.key === 'doordash' ? (hasOwnerPickup && hasRenterDropOff && !doorDashOversizedReason)
                      : hasRenterDropOff
                    return (
                      <label
                        key={opt.key}
                        className={`flex-1 flex items-center gap-3 p-3 rounded-lg border ${
                          newFulfillmentType === opt.key ? 'border-[var(--forest-green)] bg-white' : 'border-[var(--border)] bg-white'
                        } ${enabled ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                      >
                        <input
                          type="radio"
                          name="changeFulfillmentType"
                          value={opt.key}
                          checked={newFulfillmentType === opt.key}
                          disabled={!enabled || actionLoading}
                          onChange={() => setNewFulfillmentType(opt.key)}
                          className="accent-[var(--forest-green)]"
                        />
                        <i className={`mdi ${opt.icon} text-[var(--forest-green)]`}></i>
                        <span className="text-sm text-[var(--text-primary)]">{opt.label}</span>
                      </label>
                    )
                  })}
                </div>

                {/* Renter missing a shipping address: explain why DoorDash/Shipping are
                    disabled here too -- this modal is exactly where an owner lands after
                    the renter messages asking to switch to shipping, so leaving them
                    without an explanation is the worst place to omit it. */}
                {!rental.renterDropOffLocation && (
                  <p className="text-xs text-[var(--text-muted)] mt-3">
                    The renter hasn&apos;t added a shipping address, so DoorDash and Shipping aren&apos;t available. Ask them to add one in Profile &gt; Locations, or keep pickup.
                  </p>
                )}

                {doorDashOversizedReason && (
                  <p className="text-xs text-[var(--text-muted)] mt-3">
                    {doorDashOversizedReason} Shipping is still available.
                  </p>
                )}

                {newFulfillmentType === 'shipping' && newShippingQuote && (
                  <div className="mt-3 p-3 rounded-lg bg-white border border-[var(--border)] space-y-2">
                    {newShippingQuote.loading ? (
                      <p className="text-xs text-[var(--text-secondary)]">Getting a shipping quote...</p>
                    ) : (
                      <>
                        {(newShippingQuote.fromAddress || newShippingQuote.toAddress || newShippingQuote.distanceMiles != null) && (
                          <div className="text-xs text-[var(--text-secondary)] space-y-0.5">
                            {newShippingQuote.fromAddress && <div>Ship from: {newShippingQuote.fromAddress}</div>}
                            {newShippingQuote.toAddress && <div>Ship to: {newShippingQuote.toAddress}</div>}
                            {newShippingQuote.distanceMiles != null && <div>Distance: {Math.round(newShippingQuote.distanceMiles).toLocaleString()} mi</div>}
                          </div>
                        )}
                        {(rental.item.weightLbs != null || rental.item.lengthIn != null) && (
                          <div className="text-xs text-[var(--text-secondary)]">
                            Item: {rental.item.weightLbs != null ? `${rental.item.weightLbs} lbs` : 'weight not set'}
                            {rental.item.lengthIn != null && rental.item.widthIn != null && rental.item.heightIn != null
                              ? ` — ${rental.item.lengthIn}"×${rental.item.widthIn}"×${rental.item.heightIn}"`
                              : ''}
                          </div>
                        )}
                        {newShippingQuote.available ? (
                          <>
                            <p className="text-sm text-[var(--text-primary)]">
                              Shipping{newOwnerCoversShipping ? '' : ' (added to total)'}: <strong>${newShippingQuote.amount}</strong>{newShippingQuote.carrier ? ` (${newShippingQuote.carrier})` : ''}
                              {newOwnerCoversShipping && <span className="text-[var(--text-muted)]"> — covered by you, not charged to the renter</span>}
                            </p>
                            <label className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                              <input
                                type="checkbox"
                                checked={newOwnerCoversShipping}
                                onChange={(e) => setNewOwnerCoversShipping(e.target.checked)}
                                className="accent-[var(--forest-green)]"
                              />
                              I will cover the shipping cost
                            </label>
                          </>
                        ) : (
                          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1">
                            <p className="font-medium">Shipping won&apos;t be charged — no carrier rate is available for this route.</p>
                            <p>{newShippingQuote.reason}</p>
                            <p>Consider Pickup, or contact the renter.</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {newFulfillmentType && (newFulfillmentType === 'pickup' || newFulfillmentType === 'doordash') && (rental.ownerPickupLocations?.length ?? 0) > 0 && (
                  <div className="mt-3">
                    <label htmlFor="newPickupLocationLabel" className="block text-xs text-[var(--text-secondary)] mb-1">
                      Pickup location
                    </label>
                    <select
                      id="newPickupLocationLabel"
                      value={newPickupLocationLabel}
                      onChange={(e) => setNewPickupLocationLabel(e.target.value)}
                      style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '0.95rem', backgroundColor: 'white' }}
                    >
                      {rental.ownerPickupLocations!.map((loc) => (
                        <option key={loc.name} value={loc.name}>
                          {loc.name}{loc.address ? ` — ${loc.address}` : ''}{loc.isDefault ? ' (default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex gap-2 mt-4">
                  <button
                    type="button"
                    disabled={
                      actionLoading || !newFulfillmentType
                      // Switching to Shipping without a real quoted rate used to
                      // silently go through with no shipping cost at all -- an
                      // owner could switch, see nothing wrong, and the renter
                      // would never actually be charged (or told why). The amber
                      // reason box below already explains why (almost always
                      // missing weight/dimensions on the listing); block the
                      // action itself to match, the same way the initial
                      // approval panel already does for Shipping.
                      || (newFulfillmentType === 'shipping' && (!newShippingQuote || newShippingQuote.loading || !newShippingQuote.available))
                    }
                    onClick={async () => {
                      setActionLoading(true)
                      setActionError('')
                      try {
                        await rentalsApi.changeFulfillmentType(rental.id, {
                          fulfillmentType: newFulfillmentType!,
                          pickupLocationLabel: newPickupLocationLabel || undefined,
                          renterPaysShipping: !newOwnerCoversShipping,
                        })
                        await fetchRental(rental.id)
                        setShowChangeFulfillment(false)
                      } catch (err: any) {
                        setActionError(err?.response?.data?.error || 'Failed to change fulfillment type.')
                      } finally {
                        setActionLoading(false)
                      }
                    }}
                    className="btn btn-primary"
                  >
                    Save change
                  </button>
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => setShowChangeFulfillment(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {isRenter && (rental.status === 'PENDING' || rental.status === 'APPROVED') && (rental.renterShippingLocations?.length ?? 0) > 1 && (
          <div className="w-full">
            {!showChangeShipping ? (
              <button
                type="button"
                disabled={actionLoading}
                onClick={() => {
                  setNewShippingLocationLabel(rental.shippingLocationLabel || '')
                  setShowChangeShipping(true)
                }}
                className="text-sm font-medium text-[var(--forest-green)] hover:underline"
              >
                Change shipping address
              </button>
            ) : (
              <div className="w-full bg-[var(--bg-tint)] rounded-xl p-4">
                <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
                  Which shipping address should be used for this rental?
                </h3>
                <p className="text-xs text-[var(--text-secondary)] mb-3">
                  Currently: {rental.renterDropOffLocation?.name || 'not set'}
                </p>
                <div className="mt-1">
                  <label htmlFor="newShippingLocationLabel" className="block text-xs text-[var(--text-secondary)] mb-1">
                    Shipping location
                  </label>
                  <select
                    id="newShippingLocationLabel"
                    value={newShippingLocationLabel}
                    onChange={(e) => setNewShippingLocationLabel(e.target.value)}
                    style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '0.95rem', backgroundColor: 'white' }}
                  >
                    {rental.renterShippingLocations!.map((loc) => (
                      <option key={loc.name} value={loc.name}>
                        {loc.name}{loc.address ? ` — ${loc.address}` : ''}{loc.isDefault ? ' (default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    type="button"
                    disabled={actionLoading || !newShippingLocationLabel}
                    onClick={async () => {
                      setActionLoading(true)
                      setActionError('')
                      try {
                        await rentalsApi.changeShippingLocation(rental.id, {
                          shippingLocationLabel: newShippingLocationLabel,
                        })
                        await fetchRental(rental.id)
                        setShowChangeShipping(false)
                      } catch (err: any) {
                        setActionError(err?.response?.data?.error || 'Failed to change shipping address.')
                      } finally {
                        setActionLoading(false)
                      }
                    }}
                    className="btn btn-primary"
                  >
                    Save change
                  </button>
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => setShowChangeShipping(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* The owner can't initiate a return themselves -- only confirm one the renter
            has already reported (see markAsReturned/confirmReturn) -- so while ACTIVE
            they'd otherwise see no return-related action at all here. */}
        {isOwner && rental.status === 'ACTIVE' && (
          <p className="text-sm text-[var(--text-secondary)] italic">
            Waiting for the renter to return the item.
          </p>
        )}

        {isOwner && rental.status === 'RETURNED' && (
          <div className="w-full">
            <p className="text-sm text-[var(--text-secondary)] mb-3">
              {rental.renter.firstName} says they returned the item. Confirm only once you have it back. Until you do, the rental stays open.
            </p>
            <button
              disabled={actionLoading}
              onClick={() => handleAction(() => rentalsApi.confirmReturn(rental.id))}
              className="btn btn-primary"
            >
              I have the item back: confirm return
            </button>
          </div>
        )}

        {isRenter && rental.status === 'RETURNED' && (
          <p className="text-sm text-[var(--text-secondary)] italic">
            You reported the return. The rental is complete once {rental.owner.firstName} confirms they have the item back.
          </p>
        )}
      </div>

      <Modal
        isOpen={showCancelModal}
        title="Cancel rental?"
        confirmLabel={cancelLoading ? 'Cancelling...' : 'Cancel rental'}
        cancelLabel="Never mind"
        onConfirm={confirmCancelRental}
        onCancel={() => {
          if (!cancelLoading) {
            setShowCancelModal(false)
            setCancelError('')
          }
        }}
        confirmVariant="danger"
        disabled={cancelLoading}
      >
        <div>
          {cancelError ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mb-3">{cancelError}</div>
          ) : null}
          <p className="mb-4">
            {rental.isPaid
              ? 'A refund will be issued to the renter. This cannot be undone.'
              : 'This cannot be undone.'}
          </p>
          <label htmlFor="cancelReason" className="block text-sm font-medium text-[var(--text-primary)] mb-1">
            Provide a reason for the cancellation.
          </label>
          <textarea
            id="cancelReason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            disabled={cancelLoading}
            rows={3}
            placeholder="Provide a reason for the cancellation."
            className="w-full border border-[var(--border)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--forest-green)]"
          />
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Sent as a message to the other party before the rental is cancelled. This is your last chance to message them — cancelling closes the conversation.
          </p>
        </div>
      </Modal>

      <Modal
        isOpen={showExtendModal}
        title="Extend rental"
        confirmLabel={extendLoading ? 'Requesting...' : 'Request extension'}
        cancelLabel="Never mind"
        onConfirm={confirmRequestExtension}
        onCancel={() => {
          if (!extendLoading) {
            setShowExtendModal(false)
            setExtendError('')
          }
        }}
        confirmVariant="primary"
        disabled={extendLoading || !extendNewEndDate}
      >
        <div>
          {extendError ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mb-3">{extendError}</div>
          ) : null}
          <p className="mb-4 text-sm text-[var(--text-secondary)]">
            Current end date: {formatDate(rental.endDate)}. Pick a new end date — the owner will need to approve it, and you'll pay for the extra days before it takes effect.
          </p>
          <label htmlFor="extendNewEndDate" className="block text-sm font-medium text-[var(--text-primary)] mb-1">
            New end date
          </label>
          <input
            id="extendNewEndDate"
            type="date"
            value={extendNewEndDate}
            min={new Date(new Date(rental.endDate).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
            onChange={(e) => setExtendNewEndDate(e.target.value)}
            disabled={extendLoading}
            className="w-full border border-[var(--border)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--forest-green)]"
          />
        </div>
      </Modal>
      </>
    )
  }

  // Staff can open any rental to look at it; only the two people in it can act on it.
  const isParticipant = !!rental && !!user && (rental.owner.id === user.id || rental.renter.id === user.id)
  const isStaffViewer = !!rental && !!user && !isParticipant && user.role === 'ADMIN'

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <Navbar />

      <main className="min-h-[calc(100vh-var(--navbar-height))]">
        <div className="container mx-auto px-4 py-6 max-sm:pb-10">
          <div className="mb-6">
            <Link to={isStaffViewer ? '/admin' : '/my-rentals'} className="text-sm text-[var(--text-secondary)] hover:text-[var(--forest-green)]">
              {isStaffViewer ? '← Back to admin' : '← Back to My Rentals'}
            </Link>
          </div>
          {isStaffViewer && (
            <p role="status" className="mb-4 rounded-lg bg-[var(--bg-tint)] px-4 py-3 text-sm text-[var(--text-secondary)]">
              Staff view: this is the rental as its owner and renter see it. You can look but not change anything.
            </p>
          )}

          {isLoading ? (
            <div className="text-center py-12 text-[var(--text-muted)]">Loading rental details...</div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-6">
              {error}
            </div>
          ) : rental ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-xl border border-[var(--border)] p-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h1 className="text-2xl font-bold text-[var(--text-primary)]">{rental.item.title}</h1>
                      <p className="text-[var(--forest-green)] font-semibold mt-1">
                        {formatCurrency(rental.item.dailyPrice)} / day
                      </p>
                    </div>
                    <span
                      className={`text-sm font-medium px-3 py-1 rounded-full ${
                        statusClasses[rental.status] || 'bg-slate-100 text-slate-800'
                      }`}
                    >
                      {statusLabels[rental.status] || rental.status}
                    </span>
                  </div>

                  {renderImage()}

                  <div className="grid sm:grid-cols-3 gap-4 mt-6">
                    <div className="bg-[var(--bg-tint)] rounded-lg p-4">
                      <div className="text-xs text-[var(--text-secondary)] uppercase tracking-wide">Start date</div>
                      <div className="font-semibold text-[var(--text-primary)]">{formatDate(rental.startDate)}</div>
                    </div>
                    <div className="bg-[var(--bg-tint)] rounded-lg p-4">
                      <div className="text-xs text-[var(--text-secondary)] uppercase tracking-wide">End date</div>
                      <div className="font-semibold text-[var(--text-primary)]">{formatDate(rental.endDate)}</div>
                    </div>
                    <div className="bg-[var(--bg-tint)] rounded-lg p-4">
                      <div className="text-xs text-[var(--text-secondary)] uppercase tracking-wide">Total</div>
                      <div className="font-semibold text-[var(--text-primary)]">{formatCurrency(rental.totalAmount)}</div>
                    </div>
                  </div>

                  {actionError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mt-4 text-sm">
                      {actionError}
                    </div>
                  )}

                  {renderActions()}
                </div>

                {user?.id === rental.renter.id && rental.status === 'ACTIVE' && (
                  <div className="bg-white rounded-xl border border-[var(--border)] p-6">
                    <h2 className="text-lg font-bold text-[var(--text-primary)] mb-1">You are currently renting this item</h2>
                    <p className="text-sm text-[var(--text-secondary)] mb-4">What would you like to do?</p>

                    {reviewSubmitted && (
                      <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 mb-4 text-sm">
                        Thanks for your review!
                      </div>
                    )}

                    <div className="space-y-2 mb-4">
                      {([
                        { value: 'return', label: 'Start return' },
                        { value: 'problem', label: 'Report a problem' },
                        { value: 'review', label: 'Create a review' },
                      ] as const).map((opt) => (
                        <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="activeRentalAction"
                            value={opt.value}
                            checked={activeRentalAction === opt.value}
                            onChange={() => setActiveRentalAction(opt.value)}
                            className="w-4 h-4 accent-[var(--forest-green)]"
                          />
                          <span className="text-sm text-[var(--text-primary)]">{opt.label}</span>
                        </label>
                      ))}
                    </div>

                    <button
                      disabled={actionLoading}
                      onClick={handleActiveRentalGo}
                      className="btn btn-primary btn-sm"
                    >
                      Go
                    </button>
                  </div>
                )}

                <div className="bg-white rounded-xl border border-[var(--border)] p-6">
                  <h2 className="text-lg font-bold text-[var(--text-primary)] mb-4">Rental timeline</h2>
                  <div className="relative">
                    {timelineSteps.map((step, index) => {
                      const completed = step.status === 'PAID'
                        ? rental.isPaid || step.completedFor.includes(rental.status)
                        : step.completedFor.includes(rental.status)
                      const active = step.activeFor.includes(rental.status) && !completed
                      return (
                        <div key={step.status} className="flex gap-4 pb-6 last:pb-0">
                          <div className="flex flex-col items-center">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
                                completed
                                  ? 'bg-[var(--forest-green)] border-[var(--forest-green)] text-white'
                                  : active
                                  ? 'bg-white border-[var(--forest-green)] text-[var(--forest-green)]'
                                  : 'bg-white border-[var(--border)] text-[var(--text-muted)]'
                              }`}
                            >
                              {completed ? '✓' : index + 1}
                            </div>
                            {index < timelineSteps.length - 1 && (
                              <div
                                className={`w-0.5 flex-1 mt-1 ${
                                  completed ? 'bg-[var(--forest-green)]' : 'bg-[var(--border)]'
                                }`}
                              />
                            )}
                          </div>
                          <div className="pt-1">
                            <div className={`font-medium ${completed || active ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'}`}>
                              {step.status === 'PICKED_UP' && (rental.deliveryOption ?? 'pickup') !== 'pickup'
                                ? 'Item received'
                                : step.label}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {rental.conditionPhotos && (
                  <ConditionPhotos atPickup={rental.conditionPhotos.atPickup} atReturn={rental.conditionPhotos.atReturn} />
                )}

                {/* Message Thread */}
                <div
                  id="messages"
                  className="bg-white rounded-xl border border-[var(--border)] p-6"
                  style={{ scrollMarginTop: 'var(--navbar-height)' }}
                >
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <h2 className="text-lg font-bold text-[var(--text-primary)]">Messages</h2>
                    {isParticipant && <button type="button" onClick={() => setReportingUser(true)} className="min-h-[44px] px-2 text-sm text-[var(--text-secondary)] underline hover:text-[var(--text-primary)]">Report {rental.owner.id === user?.id ? 'the renter' : 'the owner'}</button>}
                  </div>
                  <div ref={messagesScrollRef} className="space-y-3 max-h-80 overflow-y-auto mb-4">
                    {messages.length === 0 ? (
                      <p className="text-sm text-[var(--text-muted)] text-center py-4">No messages yet. Start the conversation below.</p>
                    ) : (
                      messages.map((msg) => {
                        const isOwn = msg.sender.id === user?.id
                        return (
                          <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[75%] rounded-lg px-4 py-2 ${isOwn ? 'bg-[var(--forest-green)] text-white' : 'bg-[var(--bg-tint)] text-[var(--text-primary)]'}`}>
                              {!isOwn && (
                                <p className="text-xs font-medium mb-1 text-[var(--text-secondary)]">
                                  {msg.sender.firstName} {msg.sender.lastName[0]}.
                                </p>
                              )}
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              <p className={`text-xs mt-1 ${isOwn ? 'text-white/70' : 'text-[var(--text-muted)]'}`}>
                                {new Date(msg.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        )
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                  {!isParticipant ? (
                    <p className="text-sm text-[var(--text-muted)] text-center py-2 bg-[var(--bg-tint)] rounded-lg">
                      Staff view: you can read this thread but not post in it.
                    </p>
                  ) : rental.status === 'COMPLETED' || rental.status === 'CANCELLED' ? (
                    <p className="text-sm text-[var(--text-muted)] text-center py-2 bg-[var(--bg-tint)] rounded-lg">
                      This rental has ended — messaging is no longer available.
                    </p>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage() } }}
                        placeholder="Type a message..."
                        className="flex-1 min-w-0 px-3 py-2 border border-[var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--forest-green)]"
                        disabled={sendingMessage}
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || sendingMessage}
                        className="btn btn-primary whitespace-nowrap"
                      >
                        {sendingMessage ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                {/* Pickup Location -- only meaningful while the fulfillment type is
                    actually Pickup. Without the deliveryOption check, this kept
                    showing the owner's pickup address labeled "Pickup Location"
                    even after switching to Shipping, which is just wrong. */}
                {rental.pickupLocation && (rental.deliveryOption ?? 'pickup') === 'pickup' && (rental.status === 'APPROVED' || rental.status === 'ACTIVE') && (
                  <div id="pickup-location" className="bg-white rounded-xl border border-[var(--border)] p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-bold text-[var(--text-primary)]">Pickup Location</h2>
                      <button
                        disabled={actionLoading}
                        onClick={handleTrack}
                        className="btn btn-accent btn-sm"
                      >
                        <i className={`mdi ${rental.status === 'ACTIVE' ? 'mdi-map-marker-path' : 'mdi-package-variant-closed'} mr-1`}></i>
                        {rental.status === 'ACTIVE' ? 'Track rental' : 'Track pickup'}
                      </button>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="mdi mdi-map-marker text-2xl text-[var(--forest-green)]"></i>
                      <div>
                        <div className="font-semibold text-[var(--text-primary)]">{rental.pickupLocation.name}</div>
                        <div className="text-sm text-[var(--text-secondary)]">
                          {rental.pickupLocation.address}
                          {rental.pickupLocation.city && `, ${rental.pickupLocation.city}`}
                          {rental.pickupLocation.state && `, ${rental.pickupLocation.state}`}
                          {rental.pickupLocation.zip && ` ${rental.pickupLocation.zip}`}
                        </div>
                        {rental.pickupLocation.instructions && (
                          <div className="text-sm text-[var(--text-muted)] mt-2">
                            {rental.pickupLocation.instructions}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div id="payment-summary" className="bg-white rounded-xl border border-[var(--border)] p-6">
                  <h2 className="text-lg font-bold text-[var(--text-primary)] mb-4">Payment summary</h2>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between text-[var(--text-secondary)]">
                      <span>
                        Rental price
                        {rental.item.dailyPrice > 0 && (
                          <span className="text-[var(--text-muted)]">
                            {' '}({Math.round((new Date(rental.endDate).getTime() - new Date(rental.startDate).getTime()) / 86400000)} days @ {formatCurrency(rental.item.dailyPrice)}/day)
                          </span>
                        )}
                      </span>
                      <span>{formatCurrency(rental.itemPrice)}</span>
                    </div>
                    <div className="flex justify-between text-[var(--text-secondary)]">
                      <span>Service fee <span className="text-[var(--text-muted)]">({Math.round(SERVICE_FEE_PERCENT * 100)}%)</span></span>
                      <span>{formatCurrency(rental.serviceFee)}</span>
                    </div>
                    <div className="flex justify-between text-[var(--text-secondary)]">
                      <span>Deposit</span>
                      <span>{formatCurrency(rental.depositAmount)}</span>
                    </div>
                    {rental.shippingCost != null && (
                      <div className="flex justify-between text-[var(--text-secondary)]">
                        <span>
                          Shipping{rental.shippingCarrier ? ` (${rental.shippingCarrier})` : ''}
                          {rental.renterPaysShipping === false && <span className="text-[var(--text-muted)]"> — paid by owner</span>}
                        </span>
                        <span className={rental.renterPaysShipping === false ? 'line-through text-[var(--text-muted)]' : ''}>
                          {formatCurrency(rental.shippingCost)}
                        </span>
                      </div>
                    )}
                    {rental.shippingLabelTransactionId && (
                      <div className="bg-[var(--bg-primary)] rounded-lg p-3 space-y-1">
                        {rental.shippingLabelTransactionId.startsWith('SIMULATED-') && (
                          <div className="text-xs font-medium text-amber-700">(Simulated — no real label was purchased)</div>
                        )}
                        {rental.outboundTrackingNumber && (
                          <div className="flex justify-between">
                            <span className="text-[var(--text-muted)]">Tracking number</span>
                            <span className="font-mono text-[var(--text-primary)]">{rental.outboundTrackingNumber}</span>
                          </div>
                        )}
                        {user?.id === rental.owner.id && rental.shippingLabelCost != null
                          && Math.abs(Number(rental.shippingLabelCost) - Number(rental.shippingCost ?? 0)) >= 0.01 && (
                          <div className="flex justify-between">
                            <span className="text-[var(--text-muted)]">Actual shipping cost</span>
                            <span className="text-[var(--text-primary)]">{formatCurrency(rental.shippingLabelCost)}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-3 pt-1">
                          <button onClick={handleTrack} className="text-[var(--forest-green)] underline text-xs font-medium">
                            Track shipment
                          </button>
                          {user?.id === rental.owner.id && rental.shippingLabelUrl && (
                            <a
                              href={rental.shippingLabelUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[var(--forest-green)] underline text-xs font-medium"
                            >
                              Download label
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="border-t border-[var(--border)] pt-3 flex justify-between font-bold text-[var(--text-primary)]">
                      <span>Total</span>
                      <span>{formatCurrency(rental.totalAmount)}</span>
                    </div>
                    {rental.status === 'PENDING' && rental.deliveryOption === 'shipping' && (
                      <p className="text-xs text-[var(--text-muted)]">
                        + shipping cost, determined when the owner approves.
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-[var(--text-muted)]">
                        {rental.isPaid ? 'Payment received' : 'Payment pending'}
                      </span>
                      {user?.id === rental.renter.id && rental.status === 'APPROVED' && !rental.isPaid && (
                        <button
                          disabled={actionLoading}
                          onClick={handlePayNow}
                          className="btn btn-primary btn-sm"
                        >
                          Pay now
                        </button>
                      )}
                    </div>

                    {user?.id === rental.owner.id && ['ACTIVE', 'RETURNED', 'COMPLETED'].includes(rental.status) && (
                      <div className="border-t border-[var(--border)] pt-3 mt-1">
                        {rental.ownerPayoutTransferId ? (
                          <div className="text-xs text-[var(--bright-green)] font-medium">
                            Payout sent: {formatCurrency(rental.ownerPayoutAmount || rental.itemPrice)}
                            {rental.ownerPayoutAt ? ` on ${formatDate(rental.ownerPayoutAt)}` : ''}
                          </div>
                        ) : rental.ownerPayoutFailureReason ? (
                          <div className="text-xs text-[#F7A82F] font-medium">
                            Payout pending — {rental.ownerPayoutFailureReason}.{' '}
                            <Link to="/profile?tab=payouts" className="underline">Connect your Stripe account</Link>
                          </div>
                        ) : (
                          <div className="text-xs text-[var(--text-muted)]">Payout pending</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-[var(--border)] p-6">
                  <h2 className="text-lg font-bold text-[var(--text-primary)] mb-4">
                    {user?.id === rental.owner.id ? 'Renter' : 'Owner'}
                  </h2>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[var(--bg-tint)] flex items-center justify-center text-xl">
                      <i className="mdi mdi-account text-[var(--forest-green)]"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-[var(--text-primary)]">
                        {user?.id === rental.owner.id
                          ? `${rental.renter.firstName} ${rental.renter.lastName}`
                          : `${rental.owner.firstName} ${rental.owner.lastName}`}
                      </div>
                      <div className="text-xs text-[var(--text-secondary)] capitalize">
                        {user?.id === rental.renter.id ? 'You are renting this item' : `${rental.owner.verificationStatus?.toLowerCase()} verified`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {rental && (
            <>
              <ReportDialog
                isOpen={reportingUser}
                targetType="USER"
                targetId={rental.owner.id === user?.id ? rental.renter.id : rental.owner.id}
                targetLabel={rental.owner.id === user?.id ? 'the renter' : 'the owner'}
                onClose={() => setReportingUser(false)}
              />
              <Modal
                isOpen={showReportProblemModal}
                title="Report a problem"
                confirmLabel={problemSubmitting ? 'Sending...' : 'Send report'}
                cancelLabel="Cancel"
                onConfirm={handleSubmitProblem}
                onCancel={() => {
                  if (!problemSubmitting) {
                    setShowReportProblemModal(false)
                    setProblemError('')
                  }
                }}
                confirmVariant="danger"
                disabled={problemSubmitting}
              >
                <div>
                  {problemError ? (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mb-3">{problemError}</div>
                  ) : null}
                  <p className="mb-4 text-sm text-[var(--text-secondary)]">
                    Describe what's wrong. This is sent as a message to the {user?.id === rental.renter.id ? 'owner' : 'renter'} and flags the rental for review.
                  </p>
                  <label htmlFor="problemDescription" className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                    What's the problem?
                  </label>
                  <textarea
                    id="problemDescription"
                    value={problemDescription}
                    onChange={(e) => setProblemDescription(e.target.value)}
                    disabled={problemSubmitting}
                    rows={4}
                    placeholder="Describe the issue..."
                    className="w-full border border-[var(--border)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--forest-green)]"
                  />
                </div>
              </Modal>

              <Modal
                isOpen={showReviewModal}
                title="Create a review"
                confirmLabel={reviewSubmitting ? 'Submitting...' : 'Submit review'}
                cancelLabel="Cancel"
                onConfirm={handleSubmitReview}
                onCancel={() => {
                  if (!reviewSubmitting) {
                    setShowReviewModal(false)
                    setReviewError('')
                  }
                }}
                confirmVariant="primary"
                disabled={reviewSubmitting}
              >
                <div>
                  {reviewError ? (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mb-3">{reviewError}</div>
                  ) : null}
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">Rating</label>
                  <div className="flex gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setReviewRating(n)}
                        disabled={reviewSubmitting}
                        className="text-2xl leading-none"
                        style={{ color: n <= reviewRating ? '#D69E2E' : '#E2E8F0' }}
                        aria-label={`${n} star${n > 1 ? 's' : ''}`}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                  <label htmlFor="reviewContent" className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                    Comments (optional)
                  </label>
                  <textarea
                    id="reviewContent"
                    value={reviewContent}
                    onChange={(e) => setReviewContent(e.target.value)}
                    disabled={reviewSubmitting}
                    rows={3}
                    placeholder="How was the item and the owner?"
                    className="w-full border border-[var(--border)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--forest-green)] mb-3"
                  />
                  <label className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                    <input
                      type="checkbox"
                      checked={reviewWouldRentAgain}
                      onChange={(e) => setReviewWouldRentAgain(e.target.checked)}
                      disabled={reviewSubmitting}
                      className="w-4 h-4 accent-[var(--forest-green)]"
                    />
                    I would rent from this owner again
                  </label>
                </div>
              </Modal>
            </>
          )}
        </div>
      </main>
    </div>
  )
}
